import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment-hijri';
import { Calendar, ChevronRight, ChevronLeft, X } from 'lucide-react';

// أسماء الأشهر الهجرية
const HIJRI_MONTHS = [
  'محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني',
  'جمادى الأولى', 'جمادى الآخرة', 'رجب', 'شعبان',
  'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
];

// أسماء الأيام
const WEEKDAYS = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

export default function HijriDatePicker({ 
  value, 
  onChange, 
  placeholder = 'اختر التاريخ الهجري',
  label,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(moment().iYear());
  const [currentMonth, setCurrentMonth] = useState(moment().iMonth());
  const containerRef = useRef(null);

  // إغلاق عند النقر خارج المكون
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // تحديث السنة والشهر عند تغير القيمة
  useEffect(() => {
    if (value) {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const year = parseInt(parts[2]);
        if (!isNaN(year) && !isNaN(month)) {
          setCurrentYear(year);
          setCurrentMonth(month);
        }
      }
    }
  }, [value]);

  // الحصول على أيام الشهر
  const getDaysInMonth = () => {
    const firstDay = moment(`${currentYear}/${currentMonth + 1}/1`, 'iYYYY/iM/iD');
    const daysInMonth = firstDay.iDaysInMonth();
    const startDayOfWeek = firstDay.day(); // 0 = Sunday
    
    const days = [];
    
    // أيام فارغة قبل بداية الشهر
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // أيام الشهر
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  // التعامل مع اختيار يوم
  const handleDayClick = (day) => {
    if (!day) return;
    
    const formattedDate = `${day.toString().padStart(2, '0')}/${(currentMonth + 1).toString().padStart(2, '0')}/${currentYear}`;
    onChange(formattedDate);
    setIsOpen(false);
  };

  // التنقل بين الأشهر
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // التحقق من اليوم المحدد
  const isSelectedDay = (day) => {
    if (!day || !value) return false;
    const parts = value.split('/');
    if (parts.length !== 3) return false;
    return (
      parseInt(parts[0]) === day &&
      parseInt(parts[1]) === currentMonth + 1 &&
      parseInt(parts[2]) === currentYear
    );
  };

  // اليوم الحالي
  const isToday = (day) => {
    if (!day) return false;
    const today = moment();
    return (
      today.iDate() === day &&
      today.iMonth() === currentMonth &&
      today.iYear() === currentYear
    );
  };

  const days = getDaysInMonth();

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}
      
      {/* حقل الإدخال */}
      <div className="relative">
        <input
          type="text"
          value={value || ''}
          readOnly
          onClick={() => setIsOpen(!isOpen)}
          placeholder={placeholder}
          className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-gold outline-none cursor-pointer bg-white"
        />
        <Calendar 
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
        />
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* التقويم المنبثق */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white rounded-xl shadow-2xl border p-4 w-[320px]" style={{ left: 0 }}>
          {/* رأس التقويم */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2">
              {/* اختيار الشهر */}
              <select
                value={currentMonth}
                onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
                className="px-2 py-1 border rounded-lg text-sm font-semibold focus:ring-2 focus:ring-gold outline-none"
              >
                {HIJRI_MONTHS.map((month, idx) => (
                  <option key={idx} value={idx}>{month}</option>
                ))}
              </select>
              
              {/* اختيار السنة */}
              <select
                value={currentYear}
                onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                className="px-2 py-1 border rounded-lg text-sm font-semibold focus:ring-2 focus:ring-gold outline-none"
              >
                {Array.from({ length: 50 }, (_, i) => 1420 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <button
              type="button"
              onClick={goToPrevMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>

          {/* أيام الأسبوع */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* أيام الشهر */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleDayClick(day)}
                disabled={!day}
                className={`
                  h-9 w-9 rounded-lg text-sm font-medium transition-all
                  ${!day ? 'invisible' : 'hover:bg-gold/20'}
                  ${isSelectedDay(day) ? 'bg-gold text-white hover:bg-gold-dark' : ''}
                  ${isToday(day) && !isSelectedDay(day) ? 'border-2 border-gold text-gold' : ''}
                  ${day && !isSelectedDay(day) && !isToday(day) ? 'text-gray-700' : ''}
                `}
              >
                {day}
              </button>
            ))}
          </div>

          {/* زر اليوم */}
          <div className="mt-3 pt-3 border-t">
            <button
              type="button"
              onClick={() => {
                const today = moment();
                const formattedDate = `${today.iDate().toString().padStart(2, '0')}/${(today.iMonth() + 1).toString().padStart(2, '0')}/${today.iYear()}`;
                onChange(formattedDate);
                setIsOpen(false);
              }}
              className="w-full py-2 text-sm text-gold hover:bg-gold/10 rounded-lg transition-colors font-medium"
            >
              اليوم ({moment().format('iDD/iMM/iYYYY')})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// دوال التحويل المصدّرة
export const convertHijriToGregorian = (hijriDate) => {
  if (!hijriDate) return '';
  try {
    const parts = hijriDate.split('/');
    if (parts.length !== 3) return '';
    const [day, month, year] = parts;
    const m = moment(`${year}/${month}/${day}`, 'iYYYY/iM/iD');
    if (!m.isValid()) return '';
    return m.format('YYYY-MM-DD');
  } catch (e) {
    return '';
  }
};

export const convertGregorianToHijri = (gregorianDate) => {
  if (!gregorianDate) return '';
  try {
    const m = moment(gregorianDate, 'YYYY-MM-DD');
    if (!m.isValid()) return '';
    return m.format('iDD/iMM/iYYYY');
  } catch (e) {
    return '';
  }
};
